import { Brain, Calculator, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { Panel, InlineWarning } from "@/components/common/Primitives";
import { FreshnessRow } from "@/components/explain/ExplainDialogs";
import { dateTime, eur, eurSigned, num } from "@/lib/format";
import { OVERRIDE_FIELDS, inputCls } from "@/components/vehicle/constants";

const SEVERITY_TONE = { HIGH: "text-red-600", MEDIUM: "text-amber-600" };
const SEVERITY_TONE_DEFAULT = "text-slate-400";

const VAT_OPTIONS = [
  ["", "Uit brondata"],
  ["BTW aftrekbaar", "BTW aftrekbaar"],
  ["Marge-auto", "Marge-auto"],
  ["BTW niet aftrekbaar", "BTW niet aftrekbaar"],
  ["Onbekend", "Onbekend"],
];

const AI_SECTIONS = [
  ["Waarom interessant?", "waarom_interessant"],
  ["Risico's", "risicos"],
  ["Wat controleren vóór aankoop?", "controleren_voor_aankoop"],
];

export const OverridesPanel = ({
  overrides,
  setOverrides,
  co2,
  setCo2,
  vat,
  setVat,
  required,
  setRequired,
  analysis,
  listing,
  recalc,
  busy,
}) => (
  <Panel
    title="Aannames aanpassen"
    subtitle="Wijzigingen herberekenen direct de winstgevendheid"
    testid="overrides-panel"
  >
    <div className="grid grid-cols-2 gap-2 p-3">
      {OVERRIDE_FIELDS.map(([key, label]) => (
        <div key={key}>
          <label className="label-xs">{label}</label>
          <input
            type="number"
            value={overrides[key] ?? ""}
            placeholder={String(
              analysis?.costs?.assumptions_used?.[key] ??
                analysis?.costs?.lines?.find((l) => l.key === key)?.amount ??
                "",
            )}
            data-testid={`override-${key}`}
            onChange={(e) =>
              setOverrides((o) => ({
                ...o,
                [key]: e.target.value === "" ? "" : Number(e.target.value),
              }))
            }
            className={inputCls}
          />
        </div>
      ))}
      <div>
        <label className="label-xs">CO₂ WLTP handmatig (g/km)</label>
        <input
          type="number"
          value={co2}
          data-testid="override-co2"
          onChange={(e) => setCo2(e.target.value)}
          className={inputCls}
          placeholder={listing.co2_wltp ?? "onbekend"}
        />
      </div>
      <div>
        <label className="label-xs">BTW-status</label>
        <select value={vat} data-testid="override-vat" onChange={(e) => setVat(e.target.value)} className={inputCls}>
          {VAT_OPTIONS.map(([value, label]) => (
            <option key={value || "default"} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="label-xs">Vereiste handelsbijdrage</label>
        <input
          type="number"
          value={required}
          data-testid="override-required-contribution"
          onChange={(e) => setRequired(e.target.value)}
          className={inputCls}
          placeholder={String(analysis?.assumptions_snapshot?.trading?.min_contribution ?? "")}
        />
      </div>
    </div>
    <div className="flex items-center gap-2 border-t border-slate-100 px-3 py-2">
      <button
        type="button"
        onClick={recalc}
        disabled={busy}
        data-testid="recalculate-button"
        className="flex items-center gap-1.5 rounded-sm bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Calculator className="h-3.5 w-3.5" />}
        Herberekenen
      </button>
      <button
        type="button"
        onClick={() => {
          setOverrides({});
          setCo2("");
          setVat("");
          setRequired("");
        }}
        className="flex items-center gap-1.5 rounded-sm border border-slate-300 px-2.5 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
      >
        <RefreshCw className="h-3.5 w-3.5" /> Reset
      </button>
    </div>
  </Panel>
);

export const RedFlagsPanel = ({ flags, onOpenRisk }) => {
  let body;
  if (!flags.analysed) {
    body = <p className="px-3 py-3 text-[12px] text-slate-500">{flags.message}</p>;
  } else if (flags.flags.length === 0) {
    body = (
      <p className="px-3 py-3 text-[12px] text-emerald-700">
        Geen risicosignalen gedetecteerd in de advertentietekst.
      </p>
    );
  } else {
    body = (
      <div className="divide-y divide-slate-100">
        {flags.flags.map((f) => (
          <div key={f.code} className="px-3 py-2" data-testid={`red-flag-${f.code}`}>
            <div className="flex flex-wrap items-center gap-2">
              <ShieldAlert className={`h-3.5 w-3.5 ${SEVERITY_TONE[f.severity] || SEVERITY_TONE_DEFAULT}`} />
              <span className="text-[12.5px] font-semibold text-slate-800">{f.label}</span>
              <span className="chip border-slate-300 bg-slate-100 text-slate-600">
                {(f.matched_terms || [f.matched_term]).slice(0, 3).join(" · ")}
              </span>
              {f.penalty ? (
                <span className="mono text-[10.5px] font-semibold text-red-700">
                  −{num(f.penalty, { decimals: 1 })}
                </span>
              ) : (
                <span className="text-[10.5px] text-slate-500">informatief</span>
              )}
            </div>
            <p className="mt-1 text-[11px] italic leading-snug text-slate-500">{f.evidence}</p>
          </div>
        ))}
      </div>
    );
  }
  return (
    <Panel
      title="Risico-indicatoren"
      subtitle="Woordelijk bewijs uit de advertentie of providervelden — ontkenningen tellen niet mee"
      testid="red-flags-panel"
      actions={
        <div className="flex items-center gap-2">
          {(flags.counts?.high || 0) > 0 && (
            <span className="chip border-red-300 bg-red-50 text-red-800">
              {flags.counts.high}× hoog risico
            </span>
          )}
          {flags.total_penalty > 0 && (
            <span className="mono text-[11px] font-semibold text-red-700">
              −{flags.total_penalty} scorepunten
            </span>
          )}
          <button
            type="button"
            onClick={onOpenRisk}
            data-testid="open-risk-dialog"
            className="text-[11px] font-semibold text-blue-700 hover:underline"
          >
            Alles met bewijs
          </button>
        </div>
      }
    >
      {body}
      {(flags.positive_signals || []).length > 0 && (
        <div className="border-t border-emerald-200 bg-emerald-50/50 px-3 py-2">
          <div className="label-xs text-emerald-900">Positieve signalen</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {flags.positive_signals.map((p) => (
              <span key={p.code} className="chip border-emerald-300 bg-white text-emerald-800" title={p.evidence}>
                {p.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
};

export const AiPanel = ({ aiData, aiBusy, aiError, runAi }) => (
  <Panel
    title="AI-analyse"
    subtitle="Interpretatie van de deterministische uitkomsten"
    testid="ai-panel"
    actions={
      <button
        type="button"
        onClick={runAi}
        disabled={aiBusy}
        data-testid="run-ai-analysis"
        className="flex items-center gap-1.5 rounded-sm bg-slate-900 px-2.5 py-1 text-[11.5px] font-semibold text-white hover:bg-slate-700 disabled:opacity-60"
      >
        {aiBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Brain className="h-3 w-3" />}
        {aiData ? "Opnieuw analyseren" : "AI-analyse uitvoeren"}
      </button>
    }
  >
    {aiError && (
      <div className="p-3">
        <InlineWarning tone="danger">{aiError}</InlineWarning>
      </div>
    )}
    {!aiData ? (
      <p className="px-3 py-3 text-[12px] text-slate-500">
        Nog geen AI-analyse uitgevoerd. De AI ontvangt uitsluitend de door de backend berekende
        cijfers en mag deze niet aanpassen.
      </p>
    ) : (
      <div className="space-y-2.5 p-3">
        {AI_SECTIONS.map(([title, key]) => (
          <div key={title}>
            <div className="label-xs">{title}</div>
            <ul className="mt-1 space-y-0.5">
              {(aiData[key] || []).map((item) => (
                <li key={item} className="text-[12px] leading-snug text-slate-700">
                  • {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
        <div className="border-t border-slate-100 pt-2">
          <div className="label-xs">Advies</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="chip border-slate-900 bg-slate-900 text-white">{aiData.advies}</span>
            <span className="text-[11px] text-slate-500">
              Deterministisch advies: {aiData.deterministic_advice}
            </span>
          </div>
          <p className="mt-1.5 text-[12px] leading-snug text-slate-600">{aiData.toelichting_advies}</p>
          {aiData.score_uitleg && (
            <p className="mt-1 text-[11.5px] italic leading-snug text-slate-500">{aiData.score_uitleg}</p>
          )}
        </div>
        <div className="text-[10px] text-slate-400">
          Model {aiData.model} — {dateTime(aiData.created_at)}
        </div>
      </div>
    )}
  </Panel>
);

export const AuditPanel = ({ history, analysis }) => (
  <Panel title="Auditspoor" testid="audit-panel">
    <div className="divide-y divide-slate-100">
      {(history || []).slice(0, 6).map((a) => (
        <div key={a.id} className="flex items-center justify-between px-3 py-1.5">
          <span className="text-[11.5px] text-slate-600">
            {dateTime(a.created_at)} {a.is_current && <b>(actueel)</b>}
          </span>
          <span className="mono text-[11.5px] text-slate-800">
            {eurSigned(a.headline?.contribution, { fallback: "—" })} / score {a.headline?.deal_score ?? "—"}
          </span>
        </div>
      ))}
    </div>
    <div className="border-t border-slate-100 px-3 py-2">
      <FreshnessRow timestamps={analysis?.timestamps} />
    </div>
  </Panel>
);
